// v6 F9.8 — Emergent Google OAuth context.
// v6 F11   — Robust fetch (timeout + retry + classified errors).
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

import React, {
  createContext, useContext, useState, useEffect, useCallback,
} from "react";
import { authFetch, AuthFetchError } from "./authFetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // v6 F11 — laatste netwerk/auth-fout zodat LoginScreen/Layout een
  // toast of badge kan tonen i.p.v. een stille "Load failed".
  const [networkError, setNetworkError] = useState(null);

  const refresh = useCallback(async () => {
    setNetworkError(null);
    try {
      const r = await authFetch(`${API}/auth/me`, {}, { timeoutMs: 12000, retries: 2 });
      if (r.status === 401) {
        setUser(null);
        return null;
      }
      if (!r.ok) {
        setNetworkError(new AuthFetchError("server", r.status, `auth/me ${r.status}`));
        setUser(null);
        return null;
      }
      const me = await r.json();
      setUser(me);
      return me;
    } catch (e) {
      // Network/timeout — keep user unset, but expose the error to UI.
      console.error("[emma:auth] refresh failed", e);
      setNetworkError(e);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from OAuth callback (hash contains session_id),
    // skip the /me check — AuthCallback will exchange the session_id and
    // set the session cookie first, then call refresh().
    if (typeof window !== "undefined"
        && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = useCallback(() => {
    // v6 F11.1 — Ga via onze eigen backend-proxy ipv direct naar
    // auth.emergentagent.com. Server-side checkt de User-Agent en blokkeert
    // in-app webviews die Google 403'd. Diepe links die deze functie ook
    // bypassen worden zo nog steeds opgevangen omdat ze ALTIJD door /api/auth/start
    // moeten gaan om bij Google te komen.
    //
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href =
      `${API}/auth/start?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authFetch(`${API}/auth/logout`, { method: "POST" }, { timeoutMs: 5000, retries: 1 });
    } catch {
      // ignore — we'll still clear local state and redirect
    }
    setUser(null);
    window.location.href = "/login";
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, networkError }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
